import { useState } from "react";
import {
  Group,
  Title,
  Code,
  CopyButton,
  SegmentedControl,
  Stack,
  Text,
  Flex,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircleFilled,
  IconHistory,
  IconInfoCircle,
  IconMessage2,
  IconPlayerPlay,
  IconX,
} from "@tabler/icons-react";
import { Badge, Button, Spinner, ToggleItem, Tooltip } from "@onengine/component-library";
import { useFormStore } from "@/store/form-builder";
import { useAssistantStore } from "@/store/form-builder/useAssistantStore";
import { useFormAssistantAccess } from "@/components/features/FormBuilder/Assistant/useFormAssistantAccess";
import { useNavigate } from "@tanstack/react-router";
import { FormVersionHistoryDrawer } from "../FormVersionHistoryDrawer/FormVersionHistoryDrawer";
import { useIsNarrowEditor } from "../../hooks/useIsNarrowEditor";
import { HeaderOverflowDrawer, HeaderOverflowSection } from "./HeaderOverflowDrawer";
import { useEmptyQuestionsGuard } from "./useEmptyQuestionsGuard";
import { PublishConfirmModal } from "./PublishConfirmModal";
import type { FormBuilderVisibility } from "@/api/form-builder/formBuilder.types";
import styles from "./Header.module.scss";

export interface FallbackDefaultControl {
  checked: boolean;
  disabled: boolean;
  disabledReason?: string;
  onActivate: () => void;
}

export type SaveStatus = { state: "idle" | "saving" | "saved" | "error"; at?: Date };

export const formatSavedTime = (at: Date) =>
  at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const getSaveStatusLabel = ({ state, at }: SaveStatus, short = false): string | null => {
  if (state === "saving") return "Saving…";
  if (state === "saved") {
    if (!at) return "Saved";
    return short ? `Saved ${formatSavedTime(at)}` : `Saved at ${formatSavedTime(at)}`;
  }
  if (state === "error") return "Not saved";
  return null;
};

export type ReadOnlyReason = "global" | "archived" | "version";

type ReadOnlyNotice = { title: string; message: string };

const READ_ONLY_NOTICES: Record<Exclude<ReadOnlyReason, "version">, ReadOnlyNotice> = {
  global: {
    title: "Global form",
    message: "This form is managed by ServEngine and can't be edited here.",
  },
  archived: {
    title: "Archived form",
    message: "Unarchive this form from the form list to edit it.",
  },
};

const getReadOnlyNotice = (reason: ReadOnlyReason, viewingVersion?: number): ReadOnlyNotice =>
  reason === "version"
    ? {
        title: viewingVersion ? `Viewing v${viewingVersion}` : "Published version",
        message: "This is a read-only snapshot of a published version.",
      }
    : READ_ONLY_NOTICES[reason];

interface HeaderProps {
  title: string;
  onPublish?: () => void | Promise<void>;
  isSaving?: boolean;
  /** True while user-triggered publish is in progress (excludes background auto-save). */
  isPublishing?: boolean;
  formId?: string;
  tenantId?: string;
  /** True for a global form viewed outside the ServEngine super admin scope, an archived form, or a past version. */
  readOnly?: boolean;
  readOnlyReason?: ReadOnlyReason;
  viewingVersion?: number;
  /** Present only for the ServEngine-scope super admin editing a platform-owned form. */
  visibility?: FormBuilderVisibility;
  onVisibilityChange?: (next: FormBuilderVisibility) => void;
  /** Rendered next to the visibility control, so only for the ServEngine-scope super admin. */
  fallbackDefault?: FallbackDefaultControl;
  editingPublishedForm?: boolean;
  saveStatus?: SaveStatus;
  /** Why the last save failed, kept on screen until the cause is fixed. */
  saveError?: string;
  hasUnpublishedChanges?: boolean;
  confirmPublish?: boolean;
  /** Where publishing will assign this form, when it was created for an assignment slot. */
  assignTo?: string;
  /** The form is published and is being put in its slot. */
  isAssigning?: boolean;
}

// One full-width line under the header, so the editor keeps its height and the
// close button sits at the far edge instead of next to the text.
function EditorNotice({ title, message }: ReadOnlyNotice) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className={styles.noticeBar} role="status">
      <IconAlertCircleFilled size={18} className={styles.noticeIcon} aria-hidden />
      <p className={styles.noticeText}>
        <strong className={styles.noticeTitle}>{title}</strong> {message}
      </p>
      <Button
        type="icon"
        variant="tertiary"
        size="small"
        iconLeft={<IconX size={14} />}
        onClick={() => setDismissed(true)}
        htmlAttributes={{ "aria-label": `Dismiss ${title}` }}
      />
    </div>
  );
}

const HEADER_BAR_STYLE = {
  borderBottom: "1px solid #f0f2f5",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
} as const;

export function Header({
  title,
  onPublish,
  isSaving,
  isPublishing,
  formId,
  tenantId,
  readOnly,
  readOnlyReason = "global",
  viewingVersion,
  visibility,
  onVisibilityChange,
  fallbackDefault,
  editingPublishedForm,
  saveStatus,
  saveError,
  hasUnpublishedChanges,
  confirmPublish,
  assignTo,
  isAssigning,
}: HeaderProps) {
  const navigate = useNavigate();
  const setIsPreview = useFormStore((s) => s.setIsPreview);
  const setPreviewPageIndex = useFormStore((s) => s.setPreviewPageIndex);
  const getFormJSON = useFormStore((s) => s.getFormJSON);
  const getCompactJSON = useFormStore((s) => s.getCompactJSON);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jsonMode, setJsonMode] = useState<string>("compact");
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const isNarrow = useIsNarrowEditor();
  const readOnlyNotice = getReadOnlyNotice(readOnlyReason, viewingVersion);

  const json = jsonMode === "compact" ? getCompactJSON() : getFormJSON();
  const prettyJSON =
    jsonMode === "compact" ? JSON.stringify(JSON.parse(getCompactJSON()), null, 2) : getFormJSON();

  const setAssistantOpen = useAssistantStore((s) => s.setOpen);
  const isAssistantOpen = useAssistantStore((s) => s.isOpen);
  const hasAssistantConversation = useAssistantStore((s) => s.messages.length > 0);
  const hasPendingProposal = useAssistantStore((s) => s.proposal !== null);
  const canUseAssistant = useFormAssistantAccess();
  const headerActionsDisabled = readOnly || isSaving || !!isPublishing;
  const showAutoSaveLoader = !!isSaving && !isPublishing;

  const { guard: guardEmptyQuestions, dialog: emptyQuestionsDialog } = useEmptyQuestionsGuard();

  const handlePublish = () => {
    if (!onPublish) {
      setPublishOpen(true);
      return;
    }
    guardEmptyQuestions(() => {
      if (confirmPublish) setConfirmOpen(true);
      else void onPublish();
    });
  };

  const showPublishedNotice = editingPublishedForm && !readOnly;
  const saveStatusLabel = saveStatus ? getSaveStatusLabel(saveStatus) : null;
  const narrowSaveStatusLabel = saveStatus ? getSaveStatusLabel(saveStatus, true) : null;

  // The live region stays mounted while editing so a change of text is announced.
  const saveStatusBlock =
    onPublish && !readOnly ? (
      <span className={styles.saveStatus} aria-live="polite" data-state={saveStatus?.state}>
        {saveStatusLabel}
        {saveError && (
          <span className={styles.saveError} role="alert">
            {saveError}
          </span>
        )}
      </span>
    ) : null;

  const hasSaveFailure = saveStatus?.state === "error" || !!saveError;

  const unpublishedBadge =
    onPublish && !readOnly && editingPublishedForm && hasUnpublishedChanges ? (
      <Badge size="medium" variant="lowContrast" color="orange">
        Unpublished changes
      </Badge>
    ) : null;

  const confirmPublishDialog = onPublish ? (
    <>
      {emptyQuestionsDialog}
      <PublishConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          void onPublish();
        }}
      />
    </>
  ) : null;

  // Only the first open of a form's assistant lands on the preview, where the chat starts beside
  // it; otherwise the button just toggles, so an open chat always closes with one click.
  const openAssistant = () => {
    if (isAssistantOpen) {
      setAssistantOpen(false);
      return;
    }
    setAssistantOpen(true);
    if (!hasAssistantConversation) {
      setPreviewPageIndex(0);
      setIsPreview(true);
    }
  };
  const historyButton = (onClick: () => void) =>
    formId && (
      <Button
        variant="tertiary"
        size="small"
        iconLeft={<IconHistory size={14} />}
        onClick={onClick}
      >
        Version history
      </Button>
    );
  const assistantButton = (onClick: () => void) =>
    canUseAssistant &&
    !readOnly && (
      <Button
        variant="secondary"
        size="small"
        iconLeft={<IconMessage2 size={14} />}
        onClick={onClick}
        htmlAttributes={{ "aria-pressed": isAssistantOpen }}
      >
        Assistant
      </Button>
    );
  const openPreview = () => {
    setPreviewPageIndex(0);
    setIsPreview(true);
  };

  const visibilityToggle =
    onVisibilityChange && !readOnly ? (
      <ToggleItem
        checked={visibility === "ALL_TENANTS"}
        onChange={(next) => onVisibilityChange(next ? "ALL_TENANTS" : "TENANT")}
        size="sm"
        intent="brand"
        primary="Available to all entities"
      />
    ) : null;

  const fallbackReason = fallbackDefault?.disabled ? fallbackDefault.disabledReason : undefined;

  const fallbackToggle =
    !readOnly && fallbackDefault ? (
      <div className={styles.fallbackControl}>
        <ToggleItem
          checked={fallbackDefault.checked}
          disabled={fallbackDefault.disabled}
          onChange={(next) => next && fallbackDefault.onActivate()}
          size="sm"
          intent="brand"
          primary="Fallback default"
        />
        {fallbackReason && (
          <Tooltip content={fallbackReason}>
            <span
              className={styles.fallbackInfo}
              tabIndex={0}
              role="img"
              aria-label={fallbackReason}
            >
              <IconInfoCircle size={16} aria-hidden />
            </span>
          </Tooltip>
        )}
      </div>
    ) : null;

  const publishControl = (
    <Tooltip
      content="Apply or discard the assistant's proposal first"
      disabled={!hasPendingProposal}
    >
      <div>
        <Button
          variant="primary"
          size="small"
          iconLeft={<IconPlayerPlay size={14} fill="white" />}
          onClick={handlePublish}
          disabled={headerActionsDisabled || hasPendingProposal}
        >
          {isAssigning ? "Assigning..." : isPublishing ? "Publishing..." : "Publish"}
        </Button>
      </div>
    </Tooltip>
  );

  const publishJsonModal = !onPublish ? (
    <Modal
      opened={publishOpen}
      onClose={() => setPublishOpen(false)}
      title="Publish Form"
      size="lg"
      radius="lg"
    >
      <Stack gap="sm">
        <Group justify="space-between">
          <SegmentedControl
            value={jsonMode}
            onChange={setJsonMode}
            size="xs"
            data={[
              {
                label: `Compact (${(getCompactJSON().length / 1024).toFixed(1)}KB)`,
                value: "compact",
              },
              {
                label: `Full (${(getFormJSON().length / 1024).toFixed(1)}KB)`,
                value: "full",
              },
            ]}
          />
          <Text size="xs" c="dimmed">
            {jsonMode === "compact" ? "Optimized for API" : "Full data"}
          </Text>
        </Group>
        <Code block style={{ maxHeight: 400, overflow: "auto", fontSize: 12 }}>
          {prettyJSON}
        </Code>
        <Flex justify="end">
          <CopyButton value={json}>
            {({ copied, copy }) => (
              <Button variant={copied ? "secondary" : "primary"} size="small" onClick={copy}>
                {copied ? "Copied!" : "Copy JSON"}
              </Button>
            )}
          </CopyButton>
        </Flex>
      </Stack>
    </Modal>
  ) : null;

  const historyDrawer = formId ? (
    <FormVersionHistoryDrawer
      formId={formId}
      formName={title}
      tenantId={tenantId}
      opened={historyOpened}
      onClose={closeHistory}
    />
  ) : null;

  const notices = (
    <>
      {assignTo && !readOnly && (
        <EditorNotice
          title="Slot assignment"
          message={`Publishing assigns this form to ${assignTo}.`}
        />
      )}
      {readOnly && <EditorNotice title={readOnlyNotice.title} message={readOnlyNotice.message} />}
      {showPublishedNotice && (
        <EditorNotice
          title="Published form"
          message="Visits already filled keep their version; changes apply to new visits."
        />
      )}
    </>
  );

  if (isNarrow) {
    const overflowTone =
      saveStatus?.state === "error" || saveError
        ? "danger"
        : unpublishedBadge
          ? "notice"
          : undefined;
    return (
      <>
        <Group
          component="header"
          h={56}
          px="md"
          gap="xs"
          bg="white"
          wrap="nowrap"
          className={styles.narrowBar}
          style={HEADER_BAR_STYLE}
        >
          <div className={styles.narrowTitleBlock}>
            <Title order={4} c="#131927" className={styles.title}>
              {title}
            </Title>
            {onPublish && !readOnly && (
              <span
                className={styles.narrowSaveStatus}
                aria-live="polite"
                data-state={saveStatus?.state}
              >
                {narrowSaveStatusLabel}
              </span>
            )}
          </div>
          <HeaderOverflowDrawer open={moreOpen} onOpenChange={setMoreOpen} tone={overflowTone}>
            {(visibilityToggle || fallbackToggle) && (
              <HeaderOverflowSection title="Visibility">
                {visibilityToggle}
                {fallbackToggle}
              </HeaderOverflowSection>
            )}
            {(saveStatusBlock || unpublishedBadge || showAutoSaveLoader) && (
              <div className={styles.overflowStatus}>
                {onPublish && showAutoSaveLoader ? <Spinner size="small" /> : null}
                {saveStatusBlock}
                {unpublishedBadge}
                {hasSaveFailure ? (
                  <p className={styles.overflowStatusNote}>
                    Changes not saved — they&apos;ll retry automatically.
                  </p>
                ) : unpublishedBadge ? (
                  <p className={styles.overflowStatusNote}>
                    Your edits are saved as a draft and are not live until you publish.
                  </p>
                ) : null}
              </div>
            )}
            <HeaderOverflowSection title="Actions">
              {historyButton(() => {
                setMoreOpen(false);
                openHistory();
              })}
              {assistantButton(() => {
                setMoreOpen(false);
                openAssistant();
              })}
            </HeaderOverflowSection>
          </HeaderOverflowDrawer>
          <div className={styles.touchTarget}>{publishControl}</div>
          {confirmPublishDialog}
          {publishJsonModal}
          <div className={styles.touchTarget}>
            <Button
              type="icon"
              variant="tertiary"
              size="small"
              onClick={() => navigate({ to: "/form-builder" })}
              iconLeft={<IconX size={18} />}
              htmlAttributes={{ "aria-label": "Close editor" }}
            />
          </div>
        </Group>
        {historyDrawer}
        {notices}
      </>
    );
  }

  return (
    <>
      <Group
        component="header"
        h={56}
        px="lg"
        bg="white"
        justify="space-between"
        wrap="nowrap"
        style={HEADER_BAR_STYLE}
      >
        <Title order={4} c="#131927" className={styles.title}>
          {title}
        </Title>
        <Flex align={"center"} gap={20}>
          <Group gap="sm">
            {visibilityToggle}
            {fallbackToggle}
            <Flex align="center" gap="xs">
              {onPublish && showAutoSaveLoader ? <Spinner size="small" /> : null}
              {saveStatusBlock}
              {unpublishedBadge}
              {historyButton(openHistory)}
              {assistantButton(openAssistant)}
              <Button variant="secondary" size="small" onClick={openPreview}>
                Preview
              </Button>
            </Flex>
            {publishControl}
            {confirmPublishDialog}
            {publishJsonModal}
          </Group>
          <Button
            type="icon"
            variant="tertiary"
            size="small"
            onClick={() => navigate({ to: "/form-builder" })}
            iconLeft={<IconX size={18} />}
          />
        </Flex>
        {historyDrawer}
      </Group>
      {notices}
    </>
  );
}
